import { useEffect } from 'react';
import { useCompassCard } from '@scale/compass-ui/store';
import type { ArchieQCResultsAdapterContext } from '@scale/genai-compass-shared/cards/archie_qc_results_adapter';
import { FilterGroup, isValidFilterGroup } from '@scale/compass-shared/filter';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import {
  DEFAULT_ARCHIE_QC_JOIN_TYPE,
  DEFAULT_ARCHIE_QC_RESULTS_LIMIT,
  DEFAULT_ARCHIE_QC_VIEW_TYPE,
} from './constants';
import {
  ArchieQCJoinType,
  ArchieQCViewType,
} from '@scale/genai-compass-shared/cards/archie_qc_results_adapter';

function useArchieQCResultsAdapterCard(index: number) {
  const { card, updateCardContext, updateCardCanSubmit } = useCompassCard(index);
  const context = card.defaultContext as ArchieQCResultsAdapterContext;
  const projectId = useProjectId();

  const setFilter = (filter: FilterGroup) => {
    const updatedContext: ArchieQCResultsAdapterContext = {
      ...context,
      filter,
    };
    updateCardContext(updatedContext);
  };

  const setResultsLimit = (limit: number | undefined) => {
    const updatedContext: ArchieQCResultsAdapterContext = {
      ...context,
      resultsLimit: limit,
    };
    updateCardContext(updatedContext);
  };

  const setJoinType = (joinType: ArchieQCJoinType) => {
    const updatedContext: ArchieQCResultsAdapterContext = {
      ...context,
      joinType,
    };
    updateCardContext(updatedContext);
  };

  const setViewType = (viewType: ArchieQCViewType) => {
    const updatedContext: ArchieQCResultsAdapterContext = {
      ...context,
      viewType,
    };
    updateCardContext(updatedContext);
  };

  useEffect(() => {
    updateCardCanSubmit(isValidFilterGroup(context.filter));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.filter]);

  useEffect(() => {
    const updatedContext: ArchieQCResultsAdapterContext = {
      ...context,
      projectId,
    };
    updateCardContext(updatedContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Set default values if not set
  useEffect(() => {
    const updates: Partial<ArchieQCResultsAdapterContext> = {};

    // Only set resultsLimit if it's not already set in the context
    if (!('resultsLimit' in context)) {
      updates.resultsLimit = DEFAULT_ARCHIE_QC_RESULTS_LIMIT;
    }
    if (context.joinType === undefined) {
      updates.joinType = DEFAULT_ARCHIE_QC_JOIN_TYPE;
    }
    if (context.viewType === undefined) {
      updates.viewType = DEFAULT_ARCHIE_QC_VIEW_TYPE;
    }

    if (Object.keys(updates).length > 0) {
      const updatedContext: ArchieQCResultsAdapterContext = {
        ...context,
        ...updates,
      };
      updateCardContext(updatedContext);
    }
  }, [context, updateCardContext]);

  return {
    card,
    filter: context.filter,
    resultsLimit: context.resultsLimit,
    joinType: context.joinType ?? DEFAULT_ARCHIE_QC_JOIN_TYPE,
    viewType: context.viewType ?? DEFAULT_ARCHIE_QC_VIEW_TYPE,
    setFilter,
    setResultsLimit,
    setJoinType,
    setViewType,
  };
}

export default useArchieQCResultsAdapterCard;
